import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { matchesScope } from '../lib/tenantScope';
import { Plus, Search, Trash2, MapPin, Edit2 } from 'lucide-react';
import { api, getToken } from '../lib/api';
import { Dialog } from '../components/Dialog';
import { useToast } from '../components/Toast';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

export default function Departments() {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<any>({});

  // Tenants + their stats (extension count, DIDs, office-hours flag)
  const tenantsQ = useQuery<any[]>({
    queryKey: ['departments'],
    queryFn: async () => {
      const tenants: any[] = await fJson('/api/tenants');
      return Promise.all(tenants.map(async t => {
        const stats = await fJson(`/api/tenants/${t.domain_uuid}/stats`).catch(() => ({}));
        return { ...t, ...stats };
      }));
    },
  });
  // Full user list — used to render the semicolon-separated user names per
  // department (matches 3CX's "Users" column shape).
  const usersQ = useQuery<any[]>({
    queryKey: ['users'],
    queryFn: () => fJson('/api/users'),
  });
  // Group users by domain_uuid once
  const usersByDomain = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const u of (usersQ.data || [])) {
      const d = u.domain_uuid;
      if (!d) continue;
      if (!map[d]) map[d] = [];
      map[d].push(u.name || u.extension);
    }
    return map;
  }, [usersQ.data]);

  // Pagination state
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const allDepts = tenantsQ.data || [];
  const filtered = allDepts.filter((d: any) => matchesScope(d.domain_uuid)).filter((d: any) =>
    !search || (d.domain_name || '').toLowerCase().includes(search.toLowerCase())
            || (d.domain_description || '').toLowerCase().includes(search.toLowerCase()));
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const save = useMutation({
    mutationFn: () => editing
      ? fJson(`/api/tenants/${editing.domain_uuid}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) })
      : fJson(`/api/tenants`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['departments'] }); setOpen(false); setForm({}); setEditing(null); toast('success', 'Saved'); },
    onError: (e: any) => toast('error', e.message),
  });

  // Deleting a department is destructive (drops its users, extensions, trunks,
  // routing — everything), so it requires re-entering the admin password.
  const [delTarget, setDelTarget] = useState<any>(null);
  const [delPassword, setDelPassword] = useState('');
  const del = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => api.tenants.deleteVerified(id, password),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['departments'] });
      qc.invalidateQueries({ queryKey: ['users'] });
      toast('success', `Deleted ${r?.deleted || 'department'}`);
      setDelTarget(null); setDelPassword('');
    },
    onError: (e: any) => toast('error', e.message),
  });

  const [editTab, setEditTab] = useState<'general' | 'locations'>('general');
  function openAdd() { setEditing(null); setEditTab('general'); setForm({ extension_floor: 2000, extension_ceiling: 3000, system_extension_floor: 8000, system_extension_ceiling: 9000, trunk_limit: 6, transcription_mode: 'none' }); setOpen(true); }
  function openEdit(d: any) { setEditing(d); setEditTab('general'); setForm(d); setOpen(true); }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Departments</h1>
        <button onClick={openAdd} className="btn-primary inline-flex items-center gap-2"><Plus size={14} /> Add</button>
      </div>

      <div className="flex items-center gap-2 max-w-md">
        <Search size={16} className="text-textSecondary" />
        <input className="input flex-1" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" />
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Users</th>
              <th>Office Hours</th>
              <th>DIDs</th>
              <th className="w-20 text-right pr-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((d: any) => {
              const users = usersByDomain[d.domain_uuid] || [];
              const userText = users.slice(0, 8).join('; ') + (users.length > 8 ? `; +${users.length - 8} more` : '');
              const dids = Array.isArray(d.dids) ? d.dids.join(', ') : (typeof d.dids === 'string' ? d.dids : '');
              const isDefault = d.is_default || d.domain_name === 'default' || (d.domain_description || '').toUpperCase() === 'DEFAULT';
              const ohConfigured = !!d.office_hours_configured;
              return (
                <tr key={d.domain_uuid} className="hover:bg-elevated cursor-pointer" onClick={() => openEdit(d)}>
                  <td className="whitespace-nowrap">
                    {d.domain_description || d.domain_name}
                    {isDefault && <span className="text-xs text-textSecondary ml-2">(Def)</span>}
                  </td>
                  <td className="text-sm max-w-md truncate" title={users.join('; ')}>
                    {userText || <span className="text-textSecondary">—</span>}
                  </td>
                  <td>
                    {ohConfigured ? (
                      <span className="text-textPrimary">Configured</span>
                    ) : (
                      <button
                        className="text-primary hover:underline"
                        onClick={(e) => { e.stopPropagation(); navigate(`/office-hours?domain_uuid=${d.domain_uuid}`); }}
                      >Configure</button>
                    )}
                  </td>
                  <td className="text-sm">{dids}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <button className="btn-ghost p-1" title="Edit" onClick={() => openEdit(d)}><Edit2 size={15} /></button>
                      <button className="btn-ghost p-1 text-danger hover:bg-danger/10" title="Delete department" onClick={() => { setDelPassword(''); setDelTarget(d); }}><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-textSecondary">
                {filtered.length === 0 ? 'No departments yet — click + Add to create one.' : 'No matches.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination — Previous | 1 2 ... | Next */}
      {pageCount > 1 && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <button
            className="btn-ghost px-3 py-1 disabled:opacity-40"
            disabled={safePage === 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >Previous</button>
          {Array.from({ length: pageCount }, (_, i) => i + 1).map(n => (
            <button
              key={n}
              className={`px-3 py-1 rounded-md ${n === safePage ? 'bg-primary text-white' : 'hover:bg-elevated'}`}
              onClick={() => setPage(n)}
            >{n}</button>
          ))}
          <button
            className="btn-ghost px-3 py-1 disabled:opacity-40"
            disabled={safePage === pageCount}
            onClick={() => setPage(p => Math.min(pageCount, p + 1))}
          >Next</button>
        </div>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} title={editing ? 'Edit Department' : 'Add Department'} size="md"
        footer={<><button onClick={() => setOpen(false)} className="btn-ghost">Cancel</button><button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary disabled:opacity-50">{save.isPending ? 'Saving…' : 'OK'}</button></>}>
        {editing && (
          <div className="flex gap-2 border-b border-border mb-3 -mt-2">
            <button onClick={() => setEditTab('general')} className={`px-3 py-2 text-sm ${editTab === 'general' ? 'border-b-2 border-primary text-primary' : 'text-textSecondary'}`}>General</button>
            <button onClick={() => setEditTab('locations')} className={`px-3 py-2 text-sm ${editTab === 'locations' ? 'border-b-2 border-primary text-primary' : 'text-textSecondary'}`}><MapPin size={12} className="inline mr-1" />E911 Locations</button>
          </div>
        )}
        {editing && editTab === 'locations' ? (
          <DepartmentLocations domain_uuid={editing.domain_uuid} />
        ) : (
        <div className="space-y-3">
          <Field label="Department name *">
            <input className="input" value={form.domain_description || form.domain_name || ''} onChange={e => setForm({ ...form, domain_description: e.target.value, domain_name: form.domain_name || e.target.value.toLowerCase().replace(/\s+/g, '') })} placeholder="New Department" />
          </Field>
          <Field label="Department Prompt Set">
            <select className="input" value={form.prompt_set || ''} onChange={e => setForm({ ...form, prompt_set: e.target.value })}>
              <option value="">Use Global System Prompt Set (Standard English Prompts Set)</option>
              <option value="es">Spanish Prompts Set</option>
              <option value="fr">French Prompts Set</option>
            </select>
          </Field>
          <Field label="Department Language">
            <select className="input" value={form.language || ''} onChange={e => setForm({ ...form, language: e.target.value })}>
              <option value="">🇺🇸 Use Global System Language (English (US))</option>
              <option value="en-US">🇺🇸 English (US)</option>
              <option value="en-GB">🇬🇧 English (UK)</option>
              <option value="es-ES">🇪🇸 Spanish</option>
            </select>
          </Field>
          <div>
            <div className="font-medium text-sm border-b border-border pb-1 mb-2">Department Ranges</div>
            <div className="grid grid-cols-3 gap-2 items-end">
              <div className="col-span-1 text-sm text-textSecondary pb-2">User Extension Range</div>
              <Field label="From"><input type="number" className="input" value={form.extension_floor || 2000} onChange={e => setForm({ ...form, extension_floor: parseInt(e.target.value) })} /></Field>
              <Field label="To"><input type="number" className="input" value={form.extension_ceiling || 3000} onChange={e => setForm({ ...form, extension_ceiling: parseInt(e.target.value) })} /></Field>
              <div className="col-span-1 text-sm text-textSecondary pb-2">System Extension Range</div>
              <Field label="From"><input type="number" className="input" value={form.system_extension_floor || 8000} onChange={e => setForm({ ...form, system_extension_floor: parseInt(e.target.value) })} /></Field>
              <Field label="To"><input type="number" className="input" value={form.system_extension_ceiling || 9000} onChange={e => setForm({ ...form, system_extension_ceiling: parseInt(e.target.value) })} /></Field>
              <div className="col-span-1 text-sm text-textSecondary pb-2">Trunk Limit</div>
              <Field label=" "><input type="number" className="input" value={form.trunk_limit || 6} onChange={e => setForm({ ...form, trunk_limit: parseInt(e.target.value) })} /></Field>
              <div className="text-xs text-textSecondary pb-2">Trunks</div>
            </div>
          </div>
          <div>
            <div className="font-medium text-sm border-b border-border pb-1 mb-2">Transcription</div>
            <Field label="Select what to transcribe">
              <select className="input" value={form.transcription_mode || 'none'} onChange={e => setForm({ ...form, transcription_mode: e.target.value })}>
                <option value="none">No Transcription</option>
                <option value="voicemail_only">Voicemails only</option>
                <option value="always">Transcribe All Calls</option>
              </select>
            </Field>
          </div>
        </div>
        )}
      </Dialog>

      {/* Password-verified delete — destructive, so re-auth is required. */}
      <Dialog
        open={!!delTarget}
        onClose={() => { setDelTarget(null); setDelPassword(''); }}
        title="Delete department"
        size="sm"
        footer={
          <>
            <button className="btn-ghost" onClick={() => { setDelTarget(null); setDelPassword(''); }}>Cancel</button>
            <button
              className="btn bg-danger text-white hover:bg-danger/80 disabled:opacity-50"
              disabled={!delPassword || del.isPending}
              onClick={() => del.mutate({ id: delTarget.domain_uuid, password: delPassword })}
            >{del.isPending ? 'Deleting…' : 'Delete permanently'}</button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <p>You are about to permanently delete <strong>{delTarget?.domain_description || delTarget?.domain_name}</strong> and <strong>everything in it</strong> — its users, extensions, phones, trunks, call routing and history. This cannot be undone.</p>
          <p className="text-textSecondary">Enter your admin password to confirm:</p>
          <input
            type="password"
            className="input"
            autoFocus
            value={delPassword}
            placeholder="Admin password"
            onChange={(e) => setDelPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && delPassword && !del.isPending && delTarget) del.mutate({ id: delTarget.domain_uuid, password: delPassword }); }}
          />
        </div>
      </Dialog>
    </div>
  );
}

function DepartmentLocations({ domain_uuid }: { domain_uuid: string }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<any>({});
  const { data: list = [] } = useQuery({
    queryKey: ['dept-geo', domain_uuid],
    queryFn: () => api.groupGeolocation.list({ domain_uuid }),
  });
  const create = useMutation({
    mutationFn: () => api.groupGeolocation.create({ ...form, domain_uuid, group_uuid: domain_uuid }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dept-geo', domain_uuid] }); setForm({}); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.groupGeolocation.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dept-geo', domain_uuid] }),
  });

  return (
    <div className="space-y-3">
      <p className="text-xs text-textSecondary">
        Required for Kari's Law / RAY BAUM compliance. Each location is the dispatchable
        street address 911 dispatch sees when an extension here calls 911.
      </p>
      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr><th>Name</th><th>Address</th><th></th></tr></thead>
          <tbody>
            {list.map((g: any) => (
              <tr key={g.geo_uuid} className="hover:bg-elevated">
                <td className="font-medium">{g.friendly_name}</td>
                <td className="text-sm">{[g.street_address, g.city, g.state_region, g.postal_code, g.country].filter(Boolean).join(', ')}</td>
                <td><button className="btn-ghost p-1 text-danger" onClick={() => confirm('Delete?') && remove.mutate(g.geo_uuid)}><Trash2 size={14} /></button></td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={3} className="text-center py-4 text-textSecondary">No locations yet for this department.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="card p-3 grid grid-cols-2 gap-2">
        <input className="input" placeholder="Friendly name *" value={form.friendly_name || ''} onChange={e => setForm({ ...form, friendly_name: e.target.value })} />
        <input className="input" placeholder="Street address" value={form.street_address || ''} onChange={e => setForm({ ...form, street_address: e.target.value })} />
        <input className="input" placeholder="City" value={form.city || ''} onChange={e => setForm({ ...form, city: e.target.value })} />
        <input className="input" placeholder="State" value={form.state_region || ''} onChange={e => setForm({ ...form, state_region: e.target.value })} />
        <input className="input" placeholder="Postal" value={form.postal_code || ''} onChange={e => setForm({ ...form, postal_code: e.target.value })} />
        <input className="input" placeholder="Country" value={form.country || 'US'} onChange={e => setForm({ ...form, country: e.target.value })} />
        <button className="btn-primary col-span-2" disabled={!form.friendly_name || create.isPending} onClick={() => create.mutate()}>
          <Plus size={14} className="inline mr-1" /> Add location
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: any) {
  return <div><label className="block text-sm text-textSecondary mb-1">{label}</label>{children}</div>;
}
